import { uuid } from 'utils/uuid';
import { BranchableLeaf, LinkedLeaf, Leaf as ILeaf, Position } from '../../types';
import { NodeTypeKeys } from '../../utils/NodeTypeKeys';
import { Leaf } from '../Leaf';
import { BranchNameNode } from './BranchNameNode';
import { EndOfFlowNode } from './EndOfFlowNode';
import { DropzoneNode } from './DropzoneNode';

export class BranchControlNode extends Leaf implements BranchableLeaf {
  public constructor(
    public id: string,
    private typeToConfigureFor: string,
    position: Position = undefined,
    isHidden: boolean = undefined,
  ) {
    super(id, NodeTypeKeys.branchControlNodeType, [NodeTypeKeys.endOfFlowNodeType], { position, isHidden });

    this.branches = [];
  }

  private branches: Leaf[];

  public createBranch(root?: Leaf, useSettingsAsRoot?: boolean): this {
    let rootNode: Leaf;

    if (useSettingsAsRoot && root) {
      rootNode = new BranchNameNode(uuid(), this.typeToConfigureFor).setNextNode(root);
    } else if (root) {
      rootNode = root;
    } else {
      rootNode = new BranchNameNode(uuid(), this.typeToConfigureFor).setNextNode(new EndOfFlowNode(uuid()));
    }

    (rootNode as BranchNameNode).order = this.branches.length + 1;

    this.branches.push(rootNode);
    return this;
  }

  public deleteBranch(id: string, root?: Leaf) {
    const filteredBranch = this.branches.filter(s => s.id !== id);
    if (filteredBranch.length > 1) {
      return (this.branches = filteredBranch);
    } else {
      const branchNextNode = filteredBranch[0].getNextNode();
      if (branchNextNode.type === NodeTypeKeys.endOfFlowNodeType) {
        this.deleteBranchControlNode(root);
      } else {
        root.replaceNextNode(branchNextNode, true);
      }
      return root;
    }
  }

  public getBranch(id: string) {
    return this.branches.find(s => s.id === id);
  }

  public getChildren() {
    return this.branches;
  }

  public getAllNodes(): ILeaf[] {
    return this.branches.flatMap(s => s.getNodes());
  }

  public reOrderBranches(ids: string[]) {
    const orderedBranches: Leaf[] = [];

    ids.forEach((id, i) => {
      const node = Object.create(this.getNode(id));
      (node as BranchNameNode).order = i + 1;
      orderedBranches.push(node);
    });

    this.branches = orderedBranches;
  }

  public hidePlaceHolderDropNodeFor(node: ILeaf, targetId: string): this {
    this.branches.forEach(s => s.replaceNode(node, targetId));

    return this;
  }

  // Overrides

  public addNode(node: LinkedLeaf, targetId: string): this {
    this.branches.forEach(s => s.addNode(node, targetId));

    return this;
  }

  public removeNode(targetId: string): this {
    this.branches.forEach(s => s.getNodes()?.forEach(item => item.id === targetId && s.removeNode(targetId)));

    return this;
  }

  public deleteBranchControlNode(root: Leaf) {
    root.replaceNextNode(new DropzoneNode(), true).setNextNode(new EndOfFlowNode(uuid()));

    return root;
  }

  public setNextNode(node: LinkedLeaf, useAllowedTypes?: boolean): this {
    return this;
  }

  public getNodes() {
    return [this];
  }

  public getNode(target: string) {
    if (target === this.id) {
      return this;
    }

    return this.branches.flatMap(s => s.getNode(target)).filter(s => s)[0];
  }

  public showDropNodeFor(nodeType: string): this {
    this.branches.forEach(s => s.showDropNodeFor(nodeType));

    return this;
  }

  public hideDropNodeFor(nodeType: string): this {
    this.branches.forEach(s => s.hideDropNodeFor(nodeType));

    return this;
  }

  public getTypeToConfigureFor(): string {
    return this.typeToConfigureFor;
  }
}
